import React, { createContext, useContext, useReducer, useEffect, useCallback, useState, useRef } from 'react';
import { authService } from '../services/authService';
import supabase from '../services/supabaseClient';

const JWT_KEY = process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token';
const USER_KEY = process.env.REACT_APP_USER_STORAGE_KEY || 'user-info';


// Initial state
const initialState = {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    isInitializing: true,
    error: null,
};

// Action types
const AUTH_ACTIONS = {
    LOGIN_START: 'LOGIN_START',
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    LOGIN_FAILURE: 'LOGIN_FAILURE',
    LOGOUT: 'LOGOUT',
    SET_LOADING: 'SET_LOADING',
    CLEAR_ERROR: 'CLEAR_ERROR',
    SET_USER: 'SET_USER',
    INITIALIZE_COMPLETE: 'INITIALIZE_COMPLETE',
};

// Reducer
const authReducer = (state, action) => {
    switch (action.type) {
        case AUTH_ACTIONS.LOGIN_START:
            return {
                ...state,
                isLoading: true,
                error: null,
            };
        case AUTH_ACTIONS.LOGIN_SUCCESS:
            return {
                ...state,
                user: action.payload,
                isAuthenticated: true,
                isLoading: false,
                error: null,
            };
        case AUTH_ACTIONS.LOGIN_FAILURE:
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: action.payload,
            };
        case AUTH_ACTIONS.LOGOUT:
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
            };
        case AUTH_ACTIONS.SET_LOADING:
            return {
                ...state,
                isLoading: action.payload,
            };
        case AUTH_ACTIONS.SET_USER:
            return {
                ...state,
                user: action.payload,
                isAuthenticated: !!action.payload,
                isLoading: false,
            };
        case AUTH_ACTIONS.CLEAR_ERROR:
            return {
                ...state,
                error: null,
            };
        case AUTH_ACTIONS.INITIALIZE_COMPLETE:
            return {
                ...state,
                isInitializing: false,
            };
        default:
            return state;
    }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const initializingRef = useRef(false);

    // Initialize auth state - FIXED: Only run once on mount, never again
    useEffect(() => {
        if (initializingRef.current) return;
        initializingRef.current = true;

        let authSubscription = null;

        const initializeAuth = async () => {
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
            try {
                if (supabase) {
                    const handleSession = async (session) => {
                        if (session) {
                            const token = session.access_token;
                            localStorage.setItem(JWT_KEY, token);

                            // Check if there is a pending OAuth role selection
                            const pendingRole = localStorage.getItem('oauth_selected_role');
                            if (pendingRole) {
                                try {
                                    console.log('Syncing pending OAuth role:', pendingRole);
                                    await authService.syncOAuthRole(pendingRole);
                                    localStorage.removeItem('oauth_selected_role');
                                } catch (syncErr) {
                                    console.error('Error syncing OAuth role:', syncErr);
                                }
                            }

                            // Fetch user profile from backend
                            try {
                                const userProfile = await authService.getCurrentUser();
                                dispatch({ type: AUTH_ACTIONS.SET_USER, payload: userProfile });
                                localStorage.setItem(USER_KEY, JSON.stringify(userProfile));
                            } catch (err) {
                                console.error('Failed to get user info from backend after state change:', err);
                                const userType = session.user.user_metadata?.user_type || pendingRole || 'Job Seeker';
                                const fallbackUser = {
                                    userId: session.user.id,
                                    email: session.user.email,
                                    firstName: session.user.user_metadata?.first_name || session.user.user_metadata?.given_name || session.user.user_metadata?.name || 'User',
                                    lastName: session.user.user_metadata?.last_name || session.user.user_metadata?.family_name || '',
                                    userType: userType
                                };
                                dispatch({ type: AUTH_ACTIONS.SET_USER, payload: fallbackUser });
                            }
                        } else {
                            // Check if there's a traditional JWT token in localStorage
                            const localToken = localStorage.getItem(JWT_KEY);
                            const storedUser = localStorage.getItem(USER_KEY);
                            if (localToken && storedUser) {
                                try {
                                    // Verify token with backend
                                    await authService.verifyToken();
                                    const parsedUser = JSON.parse(storedUser);
                                    dispatch({ type: AUTH_ACTIONS.SET_USER, payload: parsedUser });
                                    return; // Keep the traditional login session!
                                } catch (err) {
                                    console.warn('Local token verification failed:', err);
                                }
                            }

                            // Signed out
                            localStorage.removeItem(JWT_KEY);
                            localStorage.removeItem(USER_KEY);
                            dispatch({ type: AUTH_ACTIONS.LOGOUT });
                        }
                    };

                    try {
                        // 1. Fetch initial session explicitly to guarantee loading state gets resolved
                        const { data: { session } } = await supabase.auth.getSession();
                        await handleSession(session);

                        // 2. Setup subscription to listen to future state changes
                        const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
                            console.log('Supabase Auth State Changed Event:', event);
                            // Skip initial session event as we have already handled it
                            if (event === 'INITIAL_SESSION') return;
                            await handleSession(session);
                        });
                        authSubscription = data.subscription;
                    } catch (err) {
                        console.error('Failed to initialize Supabase session:', err);
                        dispatch({ type: AUTH_ACTIONS.LOGOUT });
                    }
                } else {
                    // Fallback traditional API flow
                    if (authService.isAuthenticated()) {
                        try {
                            const storedUser = authService.getStoredUser();
                            if (storedUser) {
                                try {
                                    await authService.verifyToken();
                                    dispatch({ type: AUTH_ACTIONS.SET_USER, payload: storedUser });
                                } catch (error) {
                                    await authService.logout();
                                    dispatch({ type: AUTH_ACTIONS.LOGOUT });
                                }
                            } else {
                                const user = await authService.getCurrentUser();
                                dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
                            }
                        } catch (error) {
                            console.error('Local Auth initialization failed:', error);
                            await authService.logout();
                            dispatch({ type: AUTH_ACTIONS.LOGOUT });
                        }
                    } else {
                        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
                    }
                }
            } finally {
                dispatch({ type: AUTH_ACTIONS.INITIALIZE_COMPLETE });
                dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
            }
        };

        initializeAuth();

        return () => {
            if (authSubscription) {
                authSubscription.unsubscribe();
            }
        };
    }, []);

    // Login function - memoized to prevent re-renders
    const login = useCallback(async (credentials) => {
        dispatch({ type: AUTH_ACTIONS.LOGIN_START });
        try {
            const response = await authService.login(credentials);
            const user = {
                userId: response.userId,
                email: response.email,
                firstName: response.firstName,
                lastName: response.lastName,
                userType: response.userType,
            };
            dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: user });
            return { success: true, user };
        } catch (error) {
            const errorMessage = error.message || 'Login failed. Please try again.';
            dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage });
            return { success: false, error: errorMessage };
        }
    }, []);

    // Logout function - memoized to prevent re-renders
    const logout = useCallback(async () => {
        await authService.logout();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }, []);

    // Login with provider function - memoized to prevent re-renders
    const loginWithProvider = useCallback(async (provider, selectedRole) => {
        dispatch({ type: AUTH_ACTIONS.LOGIN_START });
        try {
            await authService.loginWithProvider(provider, selectedRole);
        } catch (error) {
            const errorMessage = error.message || 'OAuth login failed. Please try again.';
            dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage });
            return { success: false, error: errorMessage };
        }
    }, []);

    // Clear error function - memoized to prevent re-renders
    const clearError = useCallback(() => {
        dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
    }, []);

    // Refresh user profile function - memoized
    const refreshUser = useCallback(async () => {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
        try {
            const userProfile = await authService.getCurrentUser();
            dispatch({ type: AUTH_ACTIONS.SET_USER, payload: userProfile });
            localStorage.setItem(USER_KEY, JSON.stringify(userProfile));
            return userProfile;
        } catch (err) {
            console.error('Failed to refresh user profile:', err);
            throw err;
        } finally {
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
    }, []);

    const value = {
        ...state,
        login,
        logout,
        clearError,
        loginWithProvider,
        refreshUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};